/* Platform Admin — Uitnodigingen voor nieuwe KDV/BSO organisaties.
 * Alleen zichtbaar voor superadmin (Platform Super Admin).
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MailPlus, Send, Ban, MessageSquarePlus, Clock, Copy, Upload, AlertTriangle, ShieldAlert, Trash2, MessageCircle, Sparkles, Download, Archive } from "lucide-react";

const STATUS_META = {
  concept: { label: "Concept", cls: "bg-slate-100 text-slate-700" },
  verzonden: { label: "Verzonden", cls: "bg-sky-100 text-sky-700" },
  geopend: { label: "Geopend", cls: "bg-emerald-100 text-emerald-700" },
  gestart: { label: "Onboarding gestart", cls: "bg-amber-100 text-amber-800" },
  onvolledig: { label: "Onvolledig", cls: "bg-orange-100 text-orange-800" },
  ingediend: { label: "Ingediend", cls: "bg-purple-100 text-purple-700" },
  goedgekeurd: { label: "Goedgekeurd", cls: "bg-green-100 text-green-800" },
  teruggestuurd: { label: "Ter correctie", cls: "bg-yellow-100 text-yellow-800" },
  verlopen: { label: "Verlopen", cls: "bg-neutral-200 text-neutral-600" },
  ingetrokken: { label: "Ingetrokken", cls: "bg-red-100 text-red-600" },
};

const initForm = () => ({
  org_name: "",
  org_type: "KDV",
  contact_naam: "",
  contact_email: "",
  contact_telefoon: "",
  welkomsttekst: "",
  vervaldatum: (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  })(),
  interne_notitie: "",
});

export default function UitnodigingenPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(initForm());
  const [saving, setSaving] = useState(false);
  const [emailWarning, setEmailWarning] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockRows, setBlockRows] = useState([]);
  const [blockNewDomain, setBlockNewDomain] = useState("");
  const [blockNewReason, setBlockNewReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/invitations");
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon uitnodigingen niet laden");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live warning voor spam-domein / undeliverable
  useEffect(() => {
    const email = (form.contact_email || "").trim();
    if (!email || !email.includes("@")) { setEmailWarning(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/invitations/validate-address", { email });
        if (r.data?.blocked) setEmailWarning({ kind: "blocked", msg: r.data.blocked_reason });
        else if (r.data?.undeliverable) setEmailWarning({ kind: "undeliverable", msg: r.data.undeliverable_reason });
        else if (!r.data?.syntax_ok) setEmailWarning({ kind: "syntax", msg: "Ongeldig e-mailadres" });
        else setEmailWarning(null);
      } catch { setEmailWarning(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.contact_email]);

  const loadBlocklist = async () => {
    try {
      const r = await api.get("/invitations/blocklist");
      setBlockRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kon blocklist niet laden"); }
  };

  const addBlockDomain = async () => {
    const d = blockNewDomain.trim().toLowerCase().replace(/^@/, "");
    if (!d || !d.includes(".")) { toast.error("Voer een geldig domein in (bv. spam.com)"); return; }
    try {
      await api.post("/invitations/blocklist", { domain: d, reason: blockNewReason || null });
      toast.success(`Domein '${d}' toegevoegd`);
      setBlockNewDomain(""); setBlockNewReason("");
      loadBlocklist();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kon niet toevoegen"); }
  };

  const removeBlockDomain = async (d) => {
    if (!window.confirm(`Domein '${d}' van de blocklist verwijderen?`)) return;
    try {
      await api.delete(`/invitations/blocklist/${encodeURIComponent(d)}`);
      toast.success(`'${d}' verwijderd`);
      loadBlocklist();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kon niet verwijderen"); }
  };

  const openBlocklist = () => { setBlockOpen(true); loadBlocklist(); };

  // Parse CSV: eerste rij is header (org_name,org_type,contact_naam,contact_email,vervaldatum[,contact_telefoon,welkomsttekst])
  const parseCsvToRows = (csv) => {
    const lines = csv.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = (k) => headers.indexOf(k);
    const rows = [];
    const defExp = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })();
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map((c) => c.trim());
      rows.push({
        org_name: cols[idx("org_name")] || "",
        org_type: cols[idx("org_type")] || "KDV",
        contact_naam: cols[idx("contact_naam")] || "",
        contact_email: cols[idx("contact_email")] || "",
        contact_telefoon: idx("contact_telefoon") >= 0 ? cols[idx("contact_telefoon")] : null,
        welkomsttekst: idx("welkomsttekst") >= 0 ? cols[idx("welkomsttekst")] : null,
        vervaldatum: cols[idx("vervaldatum")] || defExp,
      });
    }
    return rows;
  };

  const submitBulk = async () => {
    const rowsParsed = parseCsvToRows(bulkCsv);
    if (rowsParsed.length === 0) { toast.error("Geen geldige rijen in CSV"); return; }
    setBulkBusy(true);
    try {
      const r = await api.post("/invitations/bulk", { rows: rowsParsed });
      setBulkResult(r.data);
      toast.success(`Bulk-import klaar: ${r.data.summary.created}/${r.data.summary.totaal} verstuurd`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk-import mislukt"); }
    setBulkBusy(false);
  };


  const submitCreate = async () => {
    setSaving(true);
    try {
      const r = await api.post("/invitations", form);
      const status = r.data?.status;
      const mailSent = r.data?.mail_sent;
      const mailErr = r.data?.mail_error;
      const publicLink = r.data?.public_link;
      if (mailSent === false || status === "concept") {
        const cleanErr = (mailErr || "onbekende fout").replace(/[.!?]+\s*$/, "");
        toast.error(
          `E-mail geweigerd door mailprovider: ${cleanErr}. De link is toch klaar — klik 'Kopieer link' om hem handmatig te delen.`,
          { duration: 10000 },
        );
        if (publicLink) {
          try {
            await navigator.clipboard.writeText(publicLink);
            toast.success("Uitnodigingslink automatisch gekopieerd naar klembord", { duration: 6000 });
          } catch (err) { console.debug("Clipboard write blocked:", err); }
        }
      } else {
        toast.success(`Uitnodiging verzonden naar ${form.contact_email}`);
      }
      setCreateOpen(false);
      setForm(initForm());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Aanmaken mislukt");
    }
    setSaving(false);
  };

  const resend = async (id, org) => {
    if (!window.confirm(`Uitnodiging voor "${org}" opnieuw versturen?`)) return;
    try {
      const r = await api.post(`/invitations/${id}/resend`);
      const publicLink = r.data?.public_link;
      if (r.data?.mail_sent === false) {
        const cleanErr = (r.data.mail_error || "onbekende fout").replace(/[.!?]+\s*$/, "");
        toast.error(`E-mail geweigerd: ${cleanErr}. Gebruik 'Kopieer link' om hem handmatig te delen.`, { duration: 10000 });
        if (publicLink) {
          try { await navigator.clipboard.writeText(publicLink); toast.success("Link automatisch gekopieerd", { duration: 5000 }); } catch (err) { console.debug("Clipboard write blocked:", err); }
        }
      } else {
        toast.success("Herinnering verstuurd");
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Opnieuw versturen mislukt"); }
  };

  const revoke = async (id, org) => {
    const reden = window.prompt(`Waarom trek je "${org}" in?`, "");
    if (!reden) return;
    try {
      await api.post(`/invitations/${id}/revoke`, { reden });
      toast.success("Ingetrokken");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyLink = async (id, org) => {
    let link = null;
    try {
      const r = await api.get(`/invitations/${id}/link`);
      link = r.data?.public_link;
      if (!link) { toast.error("Geen link beschikbaar"); return; }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon link niet ophalen");
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`Link voor "${org}" gekopieerd — plak in WhatsApp/e-mail`);
    } catch {
      // Clipboard access geweigerd — toon de link zodat de user hem manueel kan selecteren
      toast.info(link, { duration: 12000, description: `Kopieer link handmatig voor "${org}"` });
    }
  };

  const shareWhatsApp = async (id, org, contactNaam) => {
    try {
      const r = await api.get(`/invitations/${id}/link`);
      const link = r.data?.public_link;
      if (!link) { toast.error("Geen link beschikbaar"); return; }
      const hi = contactNaam ? `Hi ${contactNaam},` : "Hoi,";
      const text = `${hi}\n\nJe bent uitgenodigd om ${org} aan te melden bij Webster (kinderopvang-platform). Start de onboarding via onderstaande link:\n\n${link}\n\nHeb je vragen? Laat het weten.`;
      const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(waUrl, "_blank", "noopener");
      toast.success("WhatsApp geopend met kant-en-klare tekst");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon link niet ophalen");
    }
  };

  const cleanupExpired = async () => {
    if (!window.confirm("Alle verlopen uitnodigingen archiveren? Dit kan niet ongedaan worden gemaakt.")) return;
    try {
      const r = await api.post("/invitations/cleanup-expired");
      const n = r.data?.archived || 0;
      if (n === 0) toast.info("Geen verlopen uitnodigingen om op te schonen");
      else toast.success(`${n} verlopen uitnodiging${n === 1 ? "" : "en"} gearchiveerd`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Opschonen mislukt");
    }
  };

  const downloadCsvTemplate = () => {
    const rows = [
      "org_name,org_type,contact_naam,contact_email,vervaldatum,contact_telefoon,welkomsttekst",
      "Zonnestraal KDV,KDV,Anna de Vries,anna@zonnestraal.nl,2026-12-31,0612345678,Welkom bij Webster!",
      "Kleine Beertjes BSO,BSO,Piet Jansen,piet@kleinebeertjes.nl,2026-12-31,,",
    ];
    const blob = new Blob([rows.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "webster-uitnodigingen-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV-template gedownload");
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <PageHeader
        title="Onboarding uitnodigingen"
        subtitle="Nodig nieuwe KDV of BSO organisaties uit voor het platform."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={cleanupExpired}
                    data-testid="cleanup-expired-btn"
                    className="text-slate-700 hover:text-red-700 hover:border-red-200">
              <Archive className="h-4 w-4 mr-1" /> Opschonen
            </Button>
            <Button variant="outline" size="sm" onClick={openBlocklist}
                    data-testid="open-blocklist-btn">
              <ShieldAlert className="h-4 w-4 mr-1" /> Blocklist
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setBulkResult(null); setBulkOpen(true); }}
                    data-testid="open-bulk-btn">
              <Upload className="h-4 w-4 mr-1" /> Bulk-import
            </Button>
            <Button data-testid="new-invitation-btn"
                    onClick={() => setCreateOpen(true)}
                    style={{ background: "var(--sage)" }} className="text-white">
              <MailPlus className="h-4 w-4 mr-1" /> Nieuwe uitnodiging
            </Button>
          </div>
        }
      />

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invitations-table">
            <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3">Organisatie</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Contactpersoon</th>
                <th className="text-left px-4 py-3">Verloopt</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Herinneringen</th>
                <th className="text-right px-4 py-3">Acties</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center text-neutral-500 py-8">Laden…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center text-neutral-500 py-12">
                  Nog geen uitnodigingen. Klik op "Nieuwe uitnodiging" om te beginnen.
                </td></tr>
              )}
              {rows.map(r => {
                const meta = STATUS_META[r.status] || { label: r.status, cls: "bg-neutral-100" };
                const canResend = !["verlopen", "ingetrokken", "goedgekeurd"].includes(r.status);
                const canRevoke = r.status !== "goedgekeurd" && r.status !== "ingetrokken";
                return (
                  <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium">{r.org_name}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100">{r.org_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-neutral-900">{r.contact_naam}</div>
                      <div className="text-xs text-neutral-500">{r.contact_email}</div>
                    </td>
                    <td className="px-4 py-3 text-neutral-700 tabular-nums">{r.vervaldatum}</td>
                    <td className="px-4 py-3">
                      <Badge className={`${meta.cls} border-none`}>{meta.label}</Badge>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-neutral-600">
                      {r.herinnering_verzonden_count > 0 && (
                        <span className="text-xs">{r.herinnering_verzonden_count}×</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {canResend && (
                          <Button size="sm" variant="ghost" onClick={() => copyLink(r.id, r.org_name)}
                                  data-testid={`copylink-${r.id}`} title="Kopieer handmatige link"
                                  className="text-slate-600 hover:bg-slate-100">
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canResend && (
                          <Button size="sm" variant="ghost"
                                  onClick={() => shareWhatsApp(r.id, r.org_name, r.contact_naam)}
                                  data-testid={`whatsapp-${r.id}`}
                                  title="Deel via WhatsApp"
                                  className="text-emerald-600 hover:bg-emerald-50">
                            <MessageCircle className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canResend && (
                          <Button size="sm" variant="ghost" onClick={() => resend(r.id, r.org_name)}
                                  data-testid={`resend-${r.id}`} title="Opnieuw versturen">
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canRevoke && (
                          <Button size="sm" variant="ghost" onClick={() => revoke(r.id, r.org_name)}
                                  data-testid={`revoke-${r.id}`}
                                  className="text-red-600 hover:bg-red-50" title="Intrekken">
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="new-invitation-dialog">
          <DialogHeader>
            <DialogTitle>Nieuwe organisatie uitnodigen</DialogTitle>
            <DialogDescription>
              Verstuur een persoonlijke onboarding-link naar een nieuwe KDV of BSO.
              De uitnodiging bevat een unieke, tijdelijk geldige link.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Organisatienaam *</Label>
              <Input value={form.org_name} onChange={e => setForm({ ...form, org_name: e.target.value })}
                     data-testid="inv-org-name" placeholder="Kinderopvang Zonnestraal" />
            </div>
            <div>
              <Label>Type organisatie *</Label>
              <Select value={form.org_type} onValueChange={v => setForm({ ...form, org_type: v })}>
                <SelectTrigger data-testid="inv-org-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="KDV">KDV — Kinderdagverblijf</SelectItem>
                  <SelectItem value="BSO">BSO — Buitenschoolse opvang</SelectItem>
                  <SelectItem value="gecombineerd">Gecombineerd (KDV + BSO)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vervaldatum *</Label>
              <Input type="date" value={form.vervaldatum}
                     onChange={e => setForm({ ...form, vervaldatum: e.target.value })}
                     data-testid="inv-vervaldatum" />
            </div>
            <div>
              <Label>Naam contactpersoon *</Label>
              <Input value={form.contact_naam} onChange={e => setForm({ ...form, contact_naam: e.target.value })}
                     data-testid="inv-contact-naam" placeholder="Anna de Vries" />
            </div>
            <div>
              <Label>E-mailadres contactpersoon *</Label>
              <Input type="email" value={form.contact_email}
                     onChange={e => setForm({ ...form, contact_email: e.target.value })}
                     data-testid="inv-contact-email" placeholder="anna@zonnestraal.nl" />
              {emailWarning && (
                <div className={`mt-1 text-xs flex items-start gap-1.5 ${
                  emailWarning.kind === "blocked" ? "text-red-600" :
                  emailWarning.kind === "undeliverable" ? "text-amber-700" : "text-red-500"
                }`}
                     data-testid="inv-email-warning">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>{emailWarning.msg}</span>
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Telefoon (optioneel)</Label>
              <Input value={form.contact_telefoon}
                     onChange={e => setForm({ ...form, contact_telefoon: e.target.value })}
                     data-testid="inv-contact-tel" />
            </div>
            <div className="col-span-2">
              <Label>Persoonlijke welkomsttekst (optioneel)</Label>
              <Textarea rows={3} value={form.welkomsttekst}
                        onChange={e => setForm({ ...form, welkomsttekst: e.target.value })}
                        data-testid="inv-welkomst"
                        placeholder="Van harte welkom, we begeleiden u stap voor stap…" />
            </div>
            <div className="col-span-2">
              <Label>Interne notitie (niet zichtbaar voor ontvanger)</Label>
              <Textarea rows={2} value={form.interne_notitie}
                        onChange={e => setForm({ ...form, interne_notitie: e.target.value })}
                        data-testid="inv-notitie"
                        placeholder="Bijv. lead-bron, KVK-nr al bekend, telefonisch besproken…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Annuleren</Button>
            <Button onClick={submitCreate}
                    disabled={saving || !form.org_name || !form.contact_email || !form.contact_naam || emailWarning?.kind === "blocked"}
                    data-testid="inv-submit"
                    style={{ background: "var(--sage)" }} className="text-white">
              <MailPlus className="h-4 w-4 mr-1" />
              {saving ? "Verzenden…" : "Verzend uitnodiging"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Bulk-import dialog ---------------- */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-white max-w-3xl" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk-import uitnodigingen</DialogTitle>
            <DialogDescription>
              Plak CSV met kolommen: <code>org_name,org_type,contact_naam,contact_email,vervaldatum</code>
              (optioneel <code>contact_telefoon,welkomsttekst</code>). Eén regel per organisatie.
            </DialogDescription>
          </DialogHeader>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-slate-500">Nog geen CSV? Download een voorbeeldbestand:</p>
            <Button size="sm" variant="outline" onClick={downloadCsvTemplate}
                    data-testid="csv-template-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Template.csv
            </Button>
          </div>
          <Textarea rows={10} value={bulkCsv}
                    onChange={(e) => setBulkCsv(e.target.value)}
                    data-testid="bulk-csv"
                    placeholder="org_name,org_type,contact_naam,contact_email,vervaldatum&#10;Zonnestraal KDV,KDV,Anna de Vries,anna@zonnestraal.nl,2026-12-31" />
          {bulkResult && (
            <div className="mt-3 space-y-2" data-testid="bulk-result">
              <div className="text-sm flex gap-4">
                <span className="text-emerald-700">Verstuurd: <b>{bulkResult.summary.created}</b></span>
                <span className="text-amber-700">Aangemaakt zonder mail: <b>{bulkResult.summary.created_no_mail}</b></span>
                <span className="text-red-700">Geblokkeerd: <b>{bulkResult.summary.blocked}</b></span>
                <span className="text-slate-600">Overgeslagen: <b>{bulkResult.summary.skipped}</b></span>
              </div>
              <div className="max-h-40 overflow-y-auto text-xs border rounded p-2">
                {bulkResult.results.map((r, i) => (
                  <div key={i} className="flex gap-2 py-0.5">
                    <span className={
                      r.status === "created" ? "text-emerald-700" :
                      r.status === "created_no_mail" ? "text-amber-700" :
                      r.status === "blocked" ? "text-red-700" : "text-slate-500"
                    }>{r.status}</span>
                    <span className="font-mono">{r.email}</span>
                    {r.reason && <span className="text-slate-500 italic truncate">— {r.reason}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>Sluiten</Button>
            <Button onClick={submitBulk} disabled={bulkBusy || !bulkCsv.trim()}
                    data-testid="bulk-submit"
                    style={{ background: "var(--sage)" }} className="text-white">
              <Upload className="h-4 w-4 mr-1" />
              {bulkBusy ? "Bezig…" : "Verwerken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Blocklist dialog ---------------- */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="bg-white" data-testid="blocklist-dialog">
          <DialogHeader>
            <DialogTitle>Spam-domein blocklist</DialogTitle>
            <DialogDescription>
              Voeg domeinen toe die geen uitnodiging mogen ontvangen (bv. bekende spam-adressen).
              Enforcement geldt voor zowel enkele als bulk-invites.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mb-3">
            <Input placeholder="spam.com" value={blockNewDomain}
                   onChange={(e) => setBlockNewDomain(e.target.value)}
                   data-testid="blocklist-new-domain" />
            <Input placeholder="Reden (optioneel)" value={blockNewReason}
                   onChange={(e) => setBlockNewReason(e.target.value)}
                   data-testid="blocklist-new-reason" />
            <Button onClick={addBlockDomain}
                    data-testid="blocklist-add-btn"
                    style={{ background: "var(--sage)" }} className="text-white">
              Toevoegen
            </Button>
          </div>
          {/* Suggestie-chips: veel voorkomende wegwerp-/spam-domeinen */}
          <div className="mb-3">
            <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
              <Sparkles className="h-3 w-3" /> Populaire spam- en wegwerpdomeinen — klik om toe te voegen:
            </p>
            <div className="flex flex-wrap gap-1" data-testid="blocklist-suggestions">
              {["mailinator.com", "guerrillamail.com", "tempmail.com", "10minutemail.com",
                 "trashmail.com", "yopmail.com", "sharklasers.com", "getnada.com",
                 "throwawaymail.com", "dispostable.com"].filter(
                   (s) => !blockRows.some((b) => b.domain === s)
                 ).map((s) => (
                <button key={s} type="button"
                        onClick={() => setBlockNewDomain(s)}
                        data-testid={`blocklist-suggest-${s}`}
                        className="text-xs px-2 py-0.5 rounded-full bg-slate-100 hover:bg-emerald-100 text-slate-700 hover:text-emerald-900 border border-slate-200 transition">
                  + {s}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto border rounded" data-testid="blocklist-rows">
            {blockRows.length === 0 && (
              <p className="p-3 text-sm text-slate-500 italic">Nog geen geblokkeerde domeinen.</p>
            )}
            {blockRows.map((b) => (
              <div key={b.id} className="flex items-center gap-2 p-2 border-b last:border-b-0 text-sm"
                   data-testid={`blocklist-row-${b.domain}`}>
                <Ban className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                <span className="font-mono">{b.domain}</span>
                {b.reason && <span className="text-slate-500 italic text-xs">— {b.reason}</span>}
                <span className="ml-auto text-xs text-slate-400">{(b.added_at || "").slice(0, 10)}</span>
                <Button size="sm" variant="ghost" onClick={() => removeBlockDomain(b.domain)}
                        data-testid={`blocklist-remove-${b.domain}`}
                        className="h-7 px-2 text-red-600 hover:bg-red-50">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBlockOpen(false)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
